import { useAtom } from 'jotai';
import { useCallback } from 'react';

import type { MinimalCard } from '@/services/cardDatabase';
import { cardIndexBySetAtom } from '@/services/cardDatabase';
import { useAtomValue } from 'jotai';

import { setsStateAtom } from '@/features/sets/state/setsAtom';
import useCardLookup from '@/hooks/useCardLookup';

import { getAIColorCommitment, makeAIPick } from '../lib/aiPicker';
import { findBonusSheetSets, generateDraftPacks, getBoosterEligibleCards } from '../lib/boosterGenerator';
import { draftSessionAtom } from '../state/draftAtoms';
import type { AIPlayer, DraftSession, DraftStatus } from '../types';
import { PACKS_PER_DRAFT } from '../types';

export function useDraftActions() {
  const [session, setSession] = useAtom(draftSessionAtom);
  const cardsBySet = useAtomValue(cardIndexBySetAtom);
  const setsState = useAtomValue(setsStateAtom);
  const { prefetchCards } = useCardLookup();

  const startDraft = useCallback(
    async (setCode: string, setName: string, podSize: number, cardsPerPick: number) => {
      const setCards = cardsBySet.get(setCode.toLowerCase()) ?? [];
      const allSets = setsState.data ?? [];

      // Find bonus sheet
      const bonusSheetSets = findBonusSheetSets(setCode, allSets);
      const bonusSheetSetCode = bonusSheetSets[0]?.code ?? null;
      const bonusSheetCards = bonusSheetSetCode ? getBoosterEligibleCards(cardsBySet.get(bonusSheetSetCode.toLowerCase()) ?? []) : [];

      // Generate packs
      const packs = generateDraftPacks(setCards, bonusSheetCards, allSets, setCode, podSize);

      // Initialize AI players
      const aiPlayers: AIPlayer[] = Array.from({ length: podSize - 1 }, (_, i) => ({
        id: i + 1,
        pool: [],
        colorCommitment: { primary: null, secondary: null, speculating: true },
      }));

      const newSession: DraftSession = {
        id: crypto.randomUUID(),
        setCode,
        setName,
        bonusSheetSetCode,
        podSize,
        cardsPerPick,
        packs,
        currentPackIndex: 0,
        currentPickIndex: 0,
        playerPool: [],
        playerPicks: [],
        aiPlayers,
        status: 'loading',
      };

      setSession(newSession);

      // Prefetch full card data for all cards in the draft
      const allCardIds = new Set<string>();
      for (const round of packs) {
        for (const pack of round) {
          for (const card of pack.cards) {
            allCardIds.add(card.id);
          }
        }
      }
      await prefetchCards(Array.from(allCardIds));

      // Transition to picking
      setSession((prev) => (prev ? { ...prev, status: 'picking' } : prev));
    },
    [cardsBySet, setsState.data, setSession, prefetchCards],
  );

  const makePick = useCallback(
    (pickedCards: MinimalCard[]) => {
      if (!session || session.status !== 'picking') return;

      const { currentPackIndex, currentPickIndex, packs, aiPlayers, cardsPerPick } = session;

      const roundPacks = packs[currentPackIndex];
      if (!roundPacks) return;

      // Record player pick
      const playerPack = roundPacks[0];
      const newPick = {
        packIndex: currentPackIndex,
        pickIndex: currentPickIndex,
        picked: pickedCards,
        availableCards: [...playerPack.cards],
      };

      // Remove picked cards from player's pack
      const pickedIds = new Set(pickedCards.map((c) => c.id));
      const updatedPlayerPack = {
        ...playerPack,
        cards: playerPack.cards.filter((c) => !pickedIds.has(c.id)),
      };

      // AI picks from their respective packs
      const updatedAIPlayers = aiPlayers.map((player, i) => {
        const aiPackIdx = i + 1; // AI players use pack indices 1..N
        const aiPack = roundPacks[aiPackIdx];
        if (!aiPack || aiPack.cards.length === 0) return player;

        const picked = makeAIPick(player, aiPack.cards, currentPickIndex, cardsPerPick);
        const newPool = [...player.pool, ...picked];

        return {
          ...player,
          pool: newPool,
          colorCommitment: getAIColorCommitment(newPool),
        };
      });

      // Remove AI picked cards from their packs
      const updatedRoundPacks = roundPacks.map((pack, idx) => {
        if (idx === 0) return updatedPlayerPack;

        const aiPlayer = updatedAIPlayers[idx - 1];
        const lastPicked = aiPlayer.pool.slice(-cardsPerPick);
        const aiPickedIds = new Set(lastPicked.map((c) => c.id));
        return {
          ...pack,
          cards: pack.cards.filter((c) => !aiPickedIds.has(c.id)),
        };
      });

      // Rotate packs (left for packs 1&3, right for pack 2)
      const passLeft = currentPackIndex !== 1;
      const rotatedPacks = rotatePacks(updatedRoundPacks, passLeft);

      // Update round packs
      const newPacks = [...packs];
      newPacks[currentPackIndex] = rotatedPacks;

      // Check if round is complete (player's new pack is empty)
      const nextPlayerPack = rotatedPacks[0];
      const roundComplete = nextPlayerPack.cards.length === 0;

      let newStatus: DraftStatus = session.status;
      let newPackIndex = currentPackIndex;
      let newPickIndex = currentPickIndex + 1;

      if (roundComplete) {
        if (currentPackIndex < PACKS_PER_DRAFT - 1) {
          newStatus = 'between-packs';
          newPackIndex = currentPackIndex + 1;
          newPickIndex = 0;
        } else {
          newStatus = 'review';
        }
      }

      setSession({
        ...session,
        packs: newPacks,
        currentPackIndex: newPackIndex,
        currentPickIndex: newPickIndex,
        playerPool: [...session.playerPool, ...pickedCards],
        playerPicks: [...session.playerPicks, newPick],
        aiPlayers: updatedAIPlayers,
        status: newStatus,
      });
    },
    [session, setSession],
  );

  const continueDraft = useCallback(() => {
    if (!session || session.status !== 'between-packs') return;
    setSession({ ...session, status: 'picking' });
  }, [session, setSession]);

  const skipToReview = useCallback(() => {
    if (!session) return;
    setSession({ ...session, status: 'review' });
  }, [session, setSession]);

  const resetDraft = useCallback(() => {
    setSession(null);
  }, [setSession]);

  return {
    session,
    startDraft,
    makePick,
    continueDraft,
    skipToReview,
    resetDraft,
  };
}

/**
 * Rotate packs around the table.
 * passLeft = true: each player passes their pack to the left (index - 1, wrapping)
 * passLeft = false: each player passes right (index + 1, wrapping)
 */
function rotatePacks(
  packs: { cards: MinimalCard[]; originalCards: MinimalCard[] }[],
  passLeft: boolean,
): { cards: MinimalCard[]; originalCards: MinimalCard[] }[] {
  const size = packs.length;
  const rotated = new Array(size);

  for (let i = 0; i < size; i++) {
    const newIndex = passLeft ? (i - 1 + size) % size : (i + 1) % size;
    rotated[newIndex] = packs[i];
  }

  return rotated;
}
